import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CrmImportDialog from '../components/crm/CrmImportDialog';
import LeadModal from '../components/crm/LeadModal';
import PersonEditorDialog from '../components/crm/PersonEditorDialog';
import PipelineBoard from '../components/crm/PipelineBoard';
import TodayView from '../components/crm/TodayView';
import { crmService } from '../services/crm.service';
import { STAGE_LABELS, stageLabel } from '../utils/crmPipeline';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import {
  RELATIONSHIPS,
  SOURCES,
  availableBooks,
  bookLabel,
  canUseCrm,
  crmErrorMessage,
  leadTypeLabel,
  personDisplayName,
  primaryValue,
  propertyOrAreasSummary,
  relationshipLabel,
  sourceLabel,
} from '../utils/crm';
import './CrmPage.css';

const PAGE_SIZE = 25;

const BOOK_NOTE = {
  personal: 'Only you can see the people in this book.',
  brokerage: "Visible to your brokerage's broker, managers and admins. Agents cannot see this book.",
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

/**
 * CRM — the people an agent or brokerage keeps track of. Two books: "My book" (private to the
 * signed-in agent or broker) and the brokerage book (shared by the broker's team). The service
 * decides which books exist for the current role; this page only shows what it is told.
 * Personal details are encrypted at rest by the service and only decrypted for the request.
 */
/** A column heading that sorts the list; shows the current direction. */
const SortableTh = ({ label, sortKey, sort, onSort }) => {
  const active = sort.key === sortKey;
  return (
    <th aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className={`crm-sort ${active ? 'active' : ''}`} onClick={() => onSort(sortKey)}>
        {label}<span aria-hidden="true">{active ? (sort.desc ? ' ▼' : ' ▲') : ''}</span>
      </button>
    </th>
  );
};

/** "Engaged" for one side, "Buying: Engaged · Selling: New" for someone who is both. Empty if not a lead. */
const stageSummary = (p) => {
  const buy = p.buyerLead && stageLabel(p.buyerLead.stage);
  const sell = p.sellerLead && stageLabel(p.sellerLead.stage);
  if (buy && sell) return `Buying: ${buy} · Selling: ${sell}`;
  return buy || sell || '';
};

const VIEWS = [
  { value: 'today', label: 'Today' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'contacts', label: 'Contacts' },
];

const CrmPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = getActiveRoleFromStorage();
  const allowed = canUseCrm(role);

  const [booksState, setBooksState] = useState({ status: 'loading', books: [], error: '' });
  const [book, setBook] = useState(null);
  const [owner, setOwner] = useState('');

  const [queryText, setQueryText] = useState('');
  const [query, setQuery] = useState('');
  const [relationship, setRelationship] = useState('');
  const [leadTypeFilter, setLeadTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [addedFrom, setAddedFrom] = useState('');
  const [addedTo, setAddedTo] = useState('');
  const [sort, setSort] = useState({ key: 'created', desc: true });
  const [page, setPage] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ status: 'idle', items: [], total: 0, truncated: false, error: '' });

  const [view, setView] = useState('contacts');
  const [pipelineInitialView, setPipelineInitialView] = useState('ACTIVE');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [toArchive, setToArchive] = useState(null);
  const [archiveError, setArchiveError] = useState('');
  const [selection, setSelection] = useState(() => new Set());
  const [bulkOp, setBulkOp] = useState(null); // { kind: 'tag'|'relationship'|'remove', value, busy, error, done }

  // ---- books ----
  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await crmService.getBooks();
        if (cancelled) return;
        const books = availableBooks(data);
        setBooksState({ status: 'ready', books, error: '' });
        if (books.length > 0) {
          setBook(books[0].book);
          setOwner(books[0].owners[0] || '');
        }
      } catch (err) {
        if (!cancelled) setBooksState({ status: 'error', books: [], error: crmErrorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  const currentBook = useMemo(
    () => booksState.books.find((b) => b.book === book) || null,
    [booksState.books, book]
  );
  // Only a brokerage account with several brokers has to say which book; for everyone else the
  // service picks the one that exists.
  const ownerParam = book === 'brokerage' && currentBook && currentBook.owners.length > 1 ? owner : undefined;

  // ---- search debounce ----
  useEffect(() => {
    const t = setTimeout(() => { setQuery(queryText); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [queryText]);

  // ---- list ----
  useEffect(() => {
    if (!book) return undefined;
    let cancelled = false;
    setList((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const data = await crmService.listPersons(book, {
          ownerUserId: ownerParam,
          q: query,
          relationship,
          leadType: leadTypeFilter,
          source: sourceFilter,
          stage: stageFilter,
          addedFrom,
          addedTo,
          sort: sort.key,
          desc: sort.desc,
          page,
          size: PAGE_SIZE,
        });
        if (!cancelled) {
          setList({ status: 'ready', items: data.items || [], total: data.total || 0, truncated: !!data.truncated, error: '' });
        }
      } catch (err) {
        if (!cancelled) setList({ status: 'error', items: [], total: 0, truncated: false, error: crmErrorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [book, ownerParam, query, relationship, leadTypeFilter, sourceFilter, stageFilter, addedFrom, addedTo, sort, page, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Bulk selection. Filters, pagination and book changes clear the set so stale ids never
  // reach a batch — the service would reject them, but silently losing the user's picks is
  // the worse surprise.
  useEffect(() => { setSelection(new Set()); }, [book, ownerParam, query, relationship, leadTypeFilter, sourceFilter, stageFilter, addedFrom, addedTo, page, reloadKey]);

  const toggleSelect = (id) => setSelection((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllOnPage = (checked) => setSelection((s) => {
    const next = new Set(s);
    (list.items || []).forEach((p) => { if (checked) next.add(p.id); else next.delete(p.id); });
    return next;
  });
  const allSelected = list.items.length > 0 && list.items.every((p) => selection.has(p.id));

  const runBulk = async () => {
    if (!bulkOp || bulkOp.busy || bulkOp.done) return;
    setBulkOp((b) => ({ ...b, busy: true, error: '' }));
    const ids = Array.from(selection);
    const byId = Object.fromEntries(list.items.map((p) => [p.id, p]));
    try {
      const work = ids.map((id) => {
        const p = byId[id] || {};
        if (bulkOp.kind === 'remove') return crmService.archivePerson(book, id, ownerParam);
        if (bulkOp.kind === 'relationship') return crmService.updatePerson(book, id, { relationship: bulkOp.value }, ownerParam);
        // Add tag: merge with the row's current tags so existing tags are not clobbered.
        const current = Array.isArray(p.tags) ? p.tags : [];
        const next = current.includes(bulkOp.value) ? current : [...current, bulkOp.value];
        return crmService.updatePerson(book, id, { tags: next }, ownerParam);
      });
      const results = await Promise.allSettled(work);
      const failed = results.filter((r) => r.status === 'rejected').length;
      setBulkOp((b) => ({ ...b, busy: false, done: true, error: failed ? `${failed} of ${ids.length} could not be updated.` : '' }));
      setSelection(new Set());
      reload();
    } catch (err) {
      setBulkOp((b) => ({ ...b, busy: false, error: crmErrorMessage(err) }));
    }
  };

  // A KPI tile deep-links into another tab: new -> Contacts, work/won/conv -> Pipeline with a view.
  // "Going cold" (key 'cold') lands on Contacts; the dedicated filter can be wired later.
  const openKpi = useCallback((key) => {
    if (key === 'won' || key === 'conv') { setPipelineInitialView('WON'); setView('pipeline'); return; }
    if (key === 'work') { setPipelineInitialView('ACTIVE'); setView('pipeline'); return; }
    setView('contacts');
  }, []);

  const clearFilters = () => {
    setQueryText('');
    setQuery('');
    setRelationship('');
    setLeadTypeFilter('');
    setSourceFilter('');
    setStageFilter('');
    setAddedFrom('');
    setAddedTo('');
    setSort({ key: 'created', desc: true });
    setPage(0);
  };

  // Clicking a column sorts by it; clicking it again reverses. New columns start A to Z, dates newest first.
  const sortBy = (key) => {
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key === 'created' }));
    setPage(0);
  };

  const switchBook = (next) => {
    const entry = booksState.books.find((b) => b.book === next);
    setBook(next);
    setOwner(entry?.owners[0] || '');
    clearFilters();
  };

  // ---- add / open ----
  // A person opens on their own page, like a transaction. Adding happens in a dialog.
  // The open lead lives in the address (?lead=), so Back closes it and a copied link reopens it.
  const openLead = (id) => setSearchParams({ lead: id });
  const closeLead = () => setSearchParams({}, { replace: true });
  const openLeadId = searchParams.get('lead');
  const afterSaved = () => { setAdding(false); reload(); };

  // ---- archive ----
  const confirmArchive = async () => {
    try {
      await crmService.archivePerson(book, toArchive.id, ownerParam);
      setToArchive(null);
      setArchiveError('');
      reload();
    } catch (err) {
      setArchiveError(crmErrorMessage(err));
    }
  };

  // ---- render ----
  if (!allowed) {
    return (
      <div className="crm-container">
        <div className="crm-layout"><div className="crm-content">
          <h1 className="crm-title">Connect</h1>
          <p className="crm-empty" role="alert">Connect is available to agents and brokerages. Switch to one of those roles to use it.</p>
        </div></div>
      </div>
    );
  }

  const filtersActive = !!(queryText || relationship || leadTypeFilter || sourceFilter || stageFilter || addedFrom || addedTo || sort.key !== 'created' || !sort.desc);
  const totalPages = Math.max(1, Math.ceil(list.total / PAGE_SIZE));

  return (
    <div className="crm-container">
      <div className="crm-layout">
        <div className="crm-content">
          <div className="crm-header">
            <div>
              <h1 className="crm-title">Connect</h1>
              <p className="crm-subtitle">The people you work with: leads, clients, past clients, vendors and referral partners.</p>
            </div>
            {book && (
              <div className="crm-header-actions">
                <button type="button" className="crm-btn" onClick={() => setImporting(true)}>Import</button>
                <button type="button" className="crm-btn crm-btn-primary" onClick={() => setAdding(true)}>Add person</button>
              </div>
            )}
          </div>

          {booksState.status === 'loading' && <p className="crm-empty">Loading…</p>}
          {booksState.status === 'error' && <p className="crm-error" role="alert">{booksState.error}</p>}
          {booksState.status === 'ready' && booksState.books.length === 0 && (
            <p className="crm-empty">No CRM book is available for your role yet.</p>
          )}

          {book && (
            <>
              <div className="crm-tabs" role="tablist">
                {booksState.books.map((b) => (
                  <button
                    key={b.book}
                    type="button"
                    role="tab"
                    aria-selected={book === b.book}
                    className={`crm-tab ${book === b.book ? 'active' : ''}`}
                    onClick={() => switchBook(b.book)}
                  >
                    {bookLabel(b.book)}
                  </button>
                ))}
                {book === 'brokerage' && currentBook && currentBook.owners.length > 1 && (
                  <select
                    className="crm-select"
                    aria-label="Brokerage book"
                    value={owner}
                    onChange={(e) => { setOwner(e.target.value); setPage(0); }}
                  >
                    {currentBook.owners.map((id, i) => (
                      <option key={id} value={id}>{`Broker ${i + 1} (…${id.slice(-4)})`}</option>
                    ))}
                  </select>
                )}
              </div>
              <p className="crm-note">{BOOK_NOTE[book]}</p>

              <div className="crm-views" role="tablist" aria-label="CRM view">
                {VIEWS.map((v) => (
                  <button
                    key={v.value}
                    type="button"
                    role="tab"
                    aria-selected={view === v.value}
                    className={`crm-tab ${view === v.value ? 'active' : ''}`}
                    onClick={() => setView(v.value)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {view === 'today' && (
                <TodayView book={book} ownerUserId={ownerParam} reloadKey={reloadKey} onOpen={openLead} onOpenKpi={openKpi} />
              )}
              {view === 'pipeline' && (
                <PipelineBoard
                  book={book}
                  ownerUserId={ownerParam}
                  reloadKey={reloadKey}
                  onOpen={openLead}
                  onChanged={reload}
                  onOpenTransaction={(id) => navigate(`/transaction/${id}`)}
                  initialView={pipelineInitialView}
                />
              )}
              {view === 'contacts' && (<>

              <div className="crm-filters">
                <input
                  className="crm-input"
                  type="search"
                  placeholder="Search name, email, phone or tag"
                  aria-label="Search people"
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                />
                <select
                  className="crm-select"
                  aria-label="Filter by buyer or seller"
                  value={leadTypeFilter}
                  onChange={(e) => { setLeadTypeFilter(e.target.value); setPage(0); }}
                >
                  <option value="">Buyers and sellers</option>
                  <option value="BUYER">Buyers</option>
                  <option value="SELLER">Sellers</option>
                </select>
                <select
                  className="crm-select"
                  aria-label="Filter by relationship"
                  value={relationship}
                  onChange={(e) => { setRelationship(e.target.value); setPage(0); }}
                >
                  <option value="">All relationships</option>
                  {RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <select
                  className="crm-select"
                  aria-label="Filter by source"
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
                >
                  <option value="">All sources</option>
                  {SOURCES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
                <select
                  className="crm-select"
                  aria-label="Filter by stage"
                  value={stageFilter}
                  onChange={(e) => { setStageFilter(e.target.value); setPage(0); }}
                >
                  <option value="">All stages</option>
                  {Object.keys(STAGE_LABELS).map((st) => <option key={st} value={st}>{stageLabel(st)}</option>)}
                </select>
                <div className="crm-date-range">
                  <label className="crm-date-filter">Added from
                    <input className="crm-input" type="date" aria-label="Added from" value={addedFrom} max={addedTo || undefined} onChange={(e) => { setAddedFrom(e.target.value); setPage(0); }} />
                  </label>
                  <label className="crm-date-filter">to
                    <input className="crm-input" type="date" aria-label="Added to" value={addedTo} min={addedFrom || undefined} onChange={(e) => { setAddedTo(e.target.value); setPage(0); }} />
                  </label>
                </div>
                {filtersActive && (
                  <button type="button" className="crm-btn crm-btn-quiet" onClick={clearFilters}>Clear filters</button>
                )}
              </div>

              {selection.size > 0 && (
                <div className="crm-bulk-bar" role="toolbar" aria-label="Bulk actions">
                  <span className="crm-bulk-count">{selection.size} selected</span>
                  <button type="button" className="crm-btn" onClick={() => setBulkOp({ kind: 'tag', value: '', busy: false, error: '', done: false })}>Add tag</button>
                  <button type="button" className="crm-btn" onClick={() => setBulkOp({ kind: 'relationship', value: 'LEAD', busy: false, error: '', done: false })}>Change relationship</button>
                  <button type="button" className="crm-btn crm-btn-danger" onClick={() => setBulkOp({ kind: 'remove', value: null, busy: false, error: '', done: false })}>Remove</button>
                  <button type="button" className="crm-btn crm-btn-quiet" onClick={() => setSelection(new Set())}>Clear</button>
                </div>
              )}

              {list.status === 'error' && <p className="crm-error" role="alert">{list.error}</p>}
              {list.status === 'loading' && list.items.length === 0 && <p className="crm-empty">Loading…</p>}
              {list.status === 'ready' && list.items.length === 0 && (
                <p className="crm-empty">{filtersActive ? 'No one matches those filters.' : 'No people yet. Add your first person.'}</p>
              )}

              {list.items.length > 0 && (
                <div className="crm-table-wrap">
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th className="crm-td-check">
                          <input
                            type="checkbox"
                            aria-label="Select all on this page"
                            checked={allSelected}
                            onChange={(e) => selectAllOnPage(e.target.checked)}
                          />
                        </th>
                        <SortableTh label="Name" sortKey="name" sort={sort} onSort={sortBy} />
                        <SortableTh label="Buyer / seller" sortKey="type" sort={sort} onSort={sortBy} />
                        <SortableTh label="Stage" sortKey="stage" sort={sort} onSort={sortBy} />
                        <th>Property or areas of interest</th>
                        <SortableTh label="Email" sortKey="email" sort={sort} onSort={sortBy} />
                        <th>Phone</th>
                        <SortableTh label="Relationship" sortKey="relationship" sort={sort} onSort={sortBy} />
                        <SortableTh label="Source" sortKey="source" sort={sort} onSort={sortBy} />
                        <th>Tags</th>
                        <SortableTh label="Added" sortKey="created" sort={sort} onSort={sortBy} />
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {list.items.map((p) => {
                        const won  = p.buyerLead?.status === 'WON'  || p.sellerLead?.status === 'WON';
                        const lost = !won && (p.buyerLead?.status === 'LOST' || p.sellerLead?.status === 'LOST');
                        return (
                          <tr key={p.id} className={p.decryptionFailed ? 'crm-row-unreadable' : ''}>
                            <td className="crm-td-check">
                              <input
                                type="checkbox"
                                aria-label={`Select ${personDisplayName(p)}`}
                                checked={selection.has(p.id)}
                                onChange={() => toggleSelect(p.id)}
                                disabled={p.decryptionFailed}
                              />
                            </td>
                            <td>
                              {p.decryptionFailed ? (
                                <span>{personDisplayName(p)}</span>
                              ) : (
                                <button type="button" className="crm-link" onClick={() => openLead(p.id)}>{personDisplayName(p)}</button>
                              )}
                            </td>
                            <td>
                              {p.leadType && (
                                <span className={`crm-pill crm-pill-${p.leadType.toLowerCase()}`}>{leadTypeLabel(p.leadType)}</span>
                              )}
                            </td>
                            <td>
                              {stageSummary(p)}
                              {won  && <span className="crm-stage-tag crm-tag-won"  style={{ marginLeft: 6 }}>Won</span>}
                              {lost && <span className="crm-stage-tag crm-tag-lost" style={{ marginLeft: 6 }}>Lost</span>}
                            </td>
                            <td className="crm-wrap">{propertyOrAreasSummary(p)}</td>
                            <td>{primaryValue(p.emails)}</td>
                            <td className="crm-td-nowrap">{primaryValue(p.phones)}</td>
                            <td>{relationshipLabel(p.relationship)}</td>
                            <td>{sourceLabel(p.source)}</td>
                            <td>{(p.tags || []).join(', ')}</td>
                            <td className="crm-td-nowrap">{formatDate(p.createdAt)}</td>
                            <td className="crm-actions">
                              <button type="button" className="crm-btn crm-btn-quiet" onClick={() => { setArchiveError(''); setToArchive(p); }}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {list.truncated && (
                <p className="crm-note">This book is very large. Only the first part is shown. Use search to narrow it down.</p>
              )}

              {list.total > PAGE_SIZE && (
                <div className="crm-pager">
                  <button type="button" className="crm-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
                  <span>Page {page + 1} of {totalPages}</span>
                  <button type="button" className="crm-btn" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
              )}
              </>)}
            </>
          )}
        </div>
      </div>

      {/* Dialogs are portalled to <body>: inside the page they sit in a stacking context below the app header. */}
      {importing && (
        <CrmImportDialog
          book={book}
          ownerUserId={ownerParam}
          onClose={() => setImporting(false)}
          onImported={() => { setPage(0); reload(); }}
        />
      )}

      {openLeadId && book && (
        <LeadModal
          key={openLeadId}
          book={book}
          ownerUserId={ownerParam}
          personId={openLeadId}
          onClose={closeLead}
          onChanged={reload}
          onOpenTransaction={(id) => navigate(`/transaction/${id}`)}
        />
      )}

      {adding && (
        <PersonEditorDialog book={book} ownerUserId={ownerParam} person={null} onClose={() => setAdding(false)} onSaved={afterSaved} />
      )}

      {toArchive && createPortal(
        <div className="crm-modal-backdrop">
          <div className="crm-modal" role="dialog" aria-modal="true" aria-label="Remove person">
            <h2>Remove {personDisplayName(toArchive)}?</h2>
            <p>They will no longer appear in this book. Their email and phone can be added again later.</p>
            {archiveError && <p className="crm-error" role="alert">{archiveError}</p>}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={() => setToArchive(null)}>Cancel</button>
              <button type="button" className="crm-btn crm-btn-danger" onClick={confirmArchive}>Remove</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {bulkOp && createPortal(
        <div className="crm-modal-backdrop">
          <div className="crm-modal" role="dialog" aria-modal="true" aria-label="Bulk action">
            {bulkOp.kind === 'remove' && (
              <>
                <h2>Remove {selection.size} {selection.size === 1 ? 'person' : 'people'}?</h2>
                <p>They will no longer appear in this book. Their email and phone can be added again later.</p>
              </>
            )}
            {bulkOp.kind === 'tag' && (
              <>
                <h2>Add a tag to {selection.size} {selection.size === 1 ? 'person' : 'people'}</h2>
                <label>Tag
                  <input
                    className="crm-input"
                    autoFocus
                    maxLength={60}
                    value={bulkOp.value}
                    disabled={bulkOp.busy || bulkOp.done}
                    onChange={(e) => setBulkOp((b) => ({ ...b, value: e.target.value }))}
                  />
                </label>
              </>
            )}
            {bulkOp.kind === 'relationship' && (
              <>
                <h2>Change relationship for {selection.size} {selection.size === 1 ? 'person' : 'people'}</h2>
                <label>Relationship
                  <select
                    className="crm-select"
                    value={bulkOp.value}
                    disabled={bulkOp.busy || bulkOp.done}
                    onChange={(e) => setBulkOp((b) => ({ ...b, value: e.target.value }))}
                  >
                    {RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
              </>
            )}
            {bulkOp.error && <p className="crm-error" role="alert">{bulkOp.error}</p>}
            {bulkOp.done && !bulkOp.error && <p className="crm-note">Updated.</p>}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={() => setBulkOp(null)}>{bulkOp.done ? 'Close' : 'Cancel'}</button>
              {!bulkOp.done && (
                <button
                  type="button"
                  className={`crm-btn ${bulkOp.kind === 'remove' ? 'crm-btn-danger' : 'crm-btn-primary'}`}
                  disabled={bulkOp.busy || (bulkOp.kind === 'tag' && !bulkOp.value.trim())}
                  onClick={runBulk}
                >
                  {bulkOp.busy ? 'Working…' : bulkOp.kind === 'remove' ? 'Remove' : 'Apply'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CrmPage;
